import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import type { I18n } from "@lingui/core";
import {
  api,
  ApiError,
  type Customer,
  type UsageSummaryRow,
} from "../api.js";
import { useAuth, useToken } from "../auth.js";

interface RowContext {
  usage: UsageSummaryRow | undefined;
  isSuperuser: boolean;
  i18n: I18n;
  t: (descriptor: TemplateStringsArray, ...values: unknown[]) => string;
}

// Shared row renderer — the grouped and flat modes both call it so a change to
// the columns only needs to land in one place.
function renderCustomerRow(c: Customer, ctx: RowContext): ReactNode {
  const u = ctx.usage;
  return (
    <tr key={c.id}>
      <td>
        <Link to={`/customers/${c.id}`}>{c.name}</Link>
        {c.deactivatedAt && (
          <span className="badge badge-err" style={{ marginLeft: 6 }}>
            <Trans>deactivated</Trans>
          </span>
        )}
        {c.pausedAt && (
          <span className="badge badge-warn" style={{ marginLeft: 6 }}>
            <Trans>paused</Trans>
          </span>
        )}
      </td>
      <td>
        {c.contactName ?? "—"}
        <div className="muted">{c.contactEmail}</div>
      </td>
      {ctx.isSuperuser && (
        <td>
          {c.resellerId ? (
            <>
              {c.resellerName ?? c.resellerEmail}
              {c.resellerName && (
                <div className="muted small">{c.resellerEmail}</div>
              )}
            </>
          ) : (
            <span className="muted"><Trans>Direct</Trans></span>
          )}
        </td>
      )}
      <td>
        {u ? [...new Set(u.licenses.map(l => l.plan.code))].join(", ") || "—" : "—"}
      </td>
      <td style={{ textAlign: "right" }}>
        {u ? `$${u.projectedUsd.toFixed(2)}` : "—"}
      </td>
      {ctx.isSuperuser && (
        <td style={{ textAlign: "right" }}>
          {u && u.creditedUsd ? `$${u.creditedUsd.toFixed(2)}` : "—"}
        </td>
      )}
      <td>
        {u ? (
          <span>
            <Trans>{u.licenses.filter(l => l.exhausted).length} exhausted licences / {u.licenses.length}</Trans>
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>{new Date(c.createdAt).toLocaleDateString(ctx.i18n.locale)}</td>
      <td>
        <Link to={`/customers/${c.id}`}><Trans>Manage →</Trans></Link>
      </td>
    </tr>
  );
}

export function Customers() {
  const token = useToken();
  const { user, emergency } = useAuth();
  // Reseller attribution is a superuser-only view; a reseller never sees other
  // resellers' customers here anyway (the API filters the list), so hiding the
  // column keeps the table narrower for them.
  const isSuperuser = emergency || user?.role === "superuser";
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [usageRows, setUsageRows] = useState<UsageSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [sortByCost, setSortByCost] = useState(false);
  // "" = all customers, "direct" = direct Jalfa customers, or a resellerId string.
  const [resellerFilter, setResellerFilter] = useState<string>("");
  // Groups the user has explicitly collapsed. Default = all expanded, since a
  // typical fleet has a handful of resellers and full visibility is what a
  // superuser actually landing on this page usually wants.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, u] = await Promise.all([
        api.listCustomers(token),
        api.usageSummary(token).catch(() => ({ customers: [] as UsageSummaryRow[] })),
      ]);
      setCustomers(c.customers);
      setUsageRows(u.customers);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const usageByCustomer = useMemo(
    () => new Map(usageRows.map((r) => [r.customerId, r])),
    [usageRows],
  );
  // Every distinct reseller currently attached to a customer, for the filter
  // dropdown. Built from the loaded rows rather than a separate API call —
  // resellers with zero customers don't need to appear.
  const resellersInList = useMemo(() => {
    const seen = new Map<string, { id: string; label: string }>();
    for (const c of customers) {
      if (c.resellerId && !seen.has(c.resellerId)) {
        seen.set(c.resellerId, {
          id: c.resellerId,
          label: c.resellerName ?? c.resellerEmail ?? c.resellerId.slice(0, 8),
        });
      }
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    if (!resellerFilter) return customers;
    if (resellerFilter === "direct") return customers.filter((c) => c.resellerId === null);
    return customers.filter((c) => c.resellerId === resellerFilter);
  }, [customers, resellerFilter]);

  const sortCustomers = useCallback(
    (rows: Customer[]) => {
      if (!sortByCost) return rows;
      return [...rows].sort((a, b) => {
        const av = usageByCustomer.get(a.id)?.projectedUsd ?? 0;
        const bv = usageByCustomer.get(b.id)?.projectedUsd ?? 0;
        return bv - av;
      });
    },
    [sortByCost, usageByCustomer],
  );

  const sortedCustomers = useMemo(
    () => sortCustomers(filteredCustomers),
    [filteredCustomers, sortCustomers],
  );

  // Group into { direct, <resellerId>, … } for the superuser default view. We
  // group even when sortByCost is on: within each group the sort still applies,
  // and the caller can compare resellers at a glance without losing attribution.
  // A specific reseller filter disables grouping — there's only one bucket, no
  // point in wrapping it.
  interface CustomerGroup {
    key: string; // "direct" or the reseller id
    label: string;
    sublabel: string | null;
    rows: Customer[];
    totalCost: number;
  }
  const groupsForDisplay = useMemo<CustomerGroup[] | null>(() => {
    if (!isSuperuser || resellerFilter !== "") return null;
    const byKey = new Map<string, CustomerGroup>();
    for (const c of customers) {
      const key = c.resellerId ?? "direct";
      let g = byKey.get(key);
      if (!g) {
        g = {
          key,
          label:
            key === "direct"
              ? "Direct (Jalfa)"
              : c.resellerName ?? c.resellerEmail ?? key.slice(0, 8),
          sublabel: key === "direct" ? null : c.resellerName ? c.resellerEmail : null,
          rows: [],
          totalCost: 0,
        };
        byKey.set(key, g);
      }
      g.rows.push(c);
      g.totalCost += usageByCustomer.get(c.id)?.projectedUsd ?? 0;
    }
    // Direct first, then resellers alphabetically. Sort rows within each group
    // via the shared helper so the cost-sort toggle applies consistently.
    const ordered = [...byKey.values()].sort((a, b) => {
      if (a.key === "direct") return -1;
      if (b.key === "direct") return 1;
      return a.label.localeCompare(b.label);
    });
    return ordered.map((g) => ({ ...g, rows: sortCustomers(g.rows) }));
  }, [customers, isSuperuser, resellerFilter, sortCustomers, usageByCustomer]);

  const { t, i18n } = useLingui();
  // Column count for the group-header row's colspan. Superusers get two extra
  // columns (Reseller, Credited).
  const columnCount = isSuperuser ? 9 : 7;

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Customers</Trans></h1>
        <div className="page-toolbar">
          {isSuperuser && resellersInList.length > 0 && (
            <label className="filter-inline">
              <span><Trans>Reseller</Trans></span>
              <select
                value={resellerFilter}
                onChange={(e) => setResellerFilter(e.target.value)}
              >
                <option value="">{t`All`}</option>
                <option value="direct">{t`Direct (Jalfa)`}</option>
                {resellersInList.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button onClick={() => setCreating(true)}><Trans>New customer</Trans></button>
        </div>
      </header>
      {error && <div className="banner err">{error}</div>}
      {loading ? (
        <p className="muted"><Trans>Loading…</Trans></p>
      ) : customers.length === 0 ? (
        <p className="muted"><Trans>No customers yet.</Trans></p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th><Trans>Name</Trans></th>
              <th><Trans>Contact</Trans></th>
              {isSuperuser && <th><Trans>Reseller</Trans></th>}
              <th><Trans>Licence plans</Trans></th>
              <th style={{ textAlign: "right" }}>
                <button className="link-btn" onClick={() => setSortByCost(!sortByCost)}>
                  <Trans>Cost (mo)</Trans> {sortByCost ? "▾" : ""}
                </button>
              </th>
              {isSuperuser && (
                <th style={{ textAlign: "right" }}><Trans>Credited (mo)</Trans></th>
              )}
              <th><Trans>Usage</Trans></th>
              <th><Trans>Created</Trans></th>
              <th />
            </tr>
          </thead>
          {groupsForDisplay ? (
            groupsForDisplay.map((g) => {
              const collapsed = collapsedGroups.has(g.key);
              return (
                <tbody key={g.key}>
                  <tr
                    className={`group-header ${collapsed ? "collapsed" : ""}`}
                    onClick={() => toggleGroup(g.key)}
                  >
                    <td colSpan={columnCount}>
                      <span className="group-caret">▾</span>
                      <span className="group-title">{g.label}</span>
                      {g.sublabel && (
                        <span className="group-sublabel">{g.sublabel}</span>
                      )}
                      <span className="group-stats">
                        <span>
                          <span className="stat-value">{g.rows.length}</span>{" "}
                          <Trans>customers</Trans>
                        </span>
                        <span>
                          <span className="stat-value">${g.totalCost.toFixed(2)}</span>{" "}
                          <Trans>/mo</Trans>
                        </span>
                      </span>
                    </td>
                  </tr>
                  {!collapsed && g.rows.map((c) => renderCustomerRow(c, {
                    usage: usageByCustomer.get(c.id),
                    isSuperuser,
                    i18n,
                    t,
                  }))}
                </tbody>
              );
            })
          ) : (
            <tbody>
              {sortedCustomers.map((c) =>
                renderCustomerRow(c, {
                  usage: usageByCustomer.get(c.id),
                  isSuperuser,
                  i18n,
                  t,
                }),
              )}
            </tbody>
          )}
        </table>
      )}

      {creating && (
        <NewCustomerDialog
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function NewCustomerDialog({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const token = useToken();
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [company, setCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.createCustomer(token, {
        name,
        contactEmail,
        contactName: contactName || undefined,
        company: company || undefined,
        notes: notes || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2><Trans>New customer</Trans></h2>
        <form onSubmit={submit}>
          <label>
            <Trans>Name</Trans>
            <input required value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            <Trans>Contact email</Trans>
            <input
              type="email"
              required
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </label>
          <label>
            <Trans>Contact name</Trans>
            <input value={contactName} onChange={(e) => setContactName(e.target.value)} />
          </label>
          <label>
            <Trans>Company</Trans>
            <input value={company} onChange={(e) => setCompany(e.target.value)} />
          </label>
          <label>
            <Trans>Notes</Trans>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Creating…</Trans> : <Trans>Create</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
